import { useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from "react-router-dom";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import { Search, ArrowRight, Sparkles } from "lucide-react";
import { fetchSubjects } from "@/services/AdminFetchServices";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { cn } from "@/lib/utils";

const palette = [
  { accent: "#262A7A", soft: "#EEEFF9", text: "#262A7A" },
  { accent: "#0E8A78", soft: "#E6F4F2", text: "#0E8A78" },
  { accent: "#F7B030", soft: "#FFF1D6", text: "#C47D00" },
  { accent: "#2F6BD8", soft: "#EBF1FB", text: "#2F6BD8" },
  { accent: "#D9472B", soft: "#FDEEE9", text: "#D9472B" },
  { accent: "#363A96", soft: "#EEEFF9", text: "#363A96" },
  { accent: "#0E8A78", soft: "#E6F4F2", text: "#0E8A78" },
  { accent: "#F29F05", soft: "#FFF1D6", text: "#C47D00" },
  { accent: "#262A7A", soft: "#EEEFF9", text: "#262A7A" },
  { accent: "#D9472B", soft: "#FDEEE9", text: "#D9472B" },
  { accent: "#2F6BD8", soft: "#EBF1FB", text: "#2F6BD8" },
  { accent: "#0E8A78", soft: "#E6F4F2", text: "#0E8A78" },
  { accent: "#363A96", soft: "#EEEFF9", text: "#363A96" },
  { accent: "#F7B030", soft: "#FFF1D6", text: "#C47D00" },
  { accent: "#D9472B", soft: "#FDEEE9", text: "#D9472B" },
  { accent: "#262A7A", soft: "#EEEFF9", text: "#262A7A" },
];

interface Subject {
  id: string;
  name: string;
  description?: string;
}

function getInitials(name: string) {
  return name.split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function AllSubjectsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchSubjects()
      .then(data => setSubjects(data || []))
      .catch(() => setSubjects([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = subjects.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  const title = 'Browse All Subjects | BookMyCoaching';
  const description = 'Explore tutors and classes across 50+ subjects including Maths, Physics, English, and competitive exams.';
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      <main className="flex-1 pt-28">

        {/* ── Hero banner ── */}
        <div
          className="relative overflow-hidden border-b border-[#DCDDEE] py-16 sm:py-24"
          style={{ background: "linear-gradient(180deg,#fff 0%,#FFF8EA 70%,#fff 100%)" }}
        >
          {/* Decorative rings */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-16 -right-20 w-72 h-72 rounded-full border-[22px] border-[#EEEFF9]" />
            <div className="absolute -bottom-16 left-10 w-56 h-56 rounded-full border-[18px] border-[#FFF1D6]" />
          </div>

          <div className="relative container mx-auto px-4 lg:px-8 flex flex-col items-center text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#363A96] mb-4">Explore</p>

            <h1 className="font-sans text-3xl sm:text-5xl font-semibold text-[#262A7A] mb-4 leading-[1.1] tracking-tight">
              What would you like<br className="hidden sm:block" />
              <span className="relative inline-block pb-2 ml-2">
                to learn?
                <svg className="absolute bottom-0 left-0 w-full h-[12px]" viewBox="0 0 300 20" fill="none" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M6 9 C 80 3, 220 3, 294 9" stroke="#F7B030" strokeWidth="2.5" strokeLinecap="round" />
                  <path d="M22 16 C 90 11, 210 11, 278 16" stroke="#F29F05" strokeWidth="1.5" strokeLinecap="round" opacity="0.65" />
                </svg>
              </span>
            </h1>

            <p className="text-[#5A5E80] text-base sm:text-lg mb-10 max-w-md leading-relaxed">
              Browse every subject we offer and find the perfect teacher for you.
            </p>

            {/* Search */}
            <div className="relative w-full max-w-lg">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9ACD6] pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search subjects…"
                className="w-full pl-11 pr-12 py-4 rounded-2xl bg-white text-sm text-[#262A7A] placeholder:text-[#A9ACD6] outline-none border border-[#DCDDEE] shadow-md focus:ring-2 focus:ring-[#262A7A]/15 focus:border-[#262A7A] transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A9ACD6] hover:text-[#5A5E80] text-xs font-medium transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Count bar ── */}
        {!loading && (
          <div className="border-b border-border/50 bg-muted/30">
            <div className="container mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {search
                  ? <><span className="font-semibold text-foreground">{filtered.length}</span> result{filtered.length !== 1 ? "s" : ""} for "<span className="font-medium text-foreground">{search}</span>"</>
                  : <><span className="font-semibold text-foreground">{subjects.length}</span> subjects available</>
                }
              </p>
            </div>
          </div>
        )}

        {/* ── Grid ── */}
        <div className="container mx-auto px-4 lg:px-8 py-10">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-[#DCDDEE] bg-white p-5 flex flex-col gap-3 animate-pulse">
                  <div className="w-10 h-10 rounded-xl bg-[#EEEFF9]" />
                  <div className="h-3 w-3/4 bg-[#EEEFF9] rounded-full" />
                  <div className="h-2.5 w-1/2 bg-[#EEEFF9]/60 rounded-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-28 text-center">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-5">
                <Search className="w-8 h-8 text-muted-foreground/40" />
              </div>
              <p className="text-lg font-semibold text-foreground mb-1">No subjects found</p>
              <p className="text-sm text-muted-foreground mb-4">Try a different search term.</p>
              <button
                onClick={() => setSearch("")}
                className="text-sm font-medium text-primary hover:underline"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filtered.map((subject, idx) => {
                const p = palette[idx % palette.length];
                return (
                  <button
                    key={subject.id}
                    onClick={() => navigate(`/search?q=${encodeURIComponent(subject.name)}`)}
                    className="group relative flex flex-col items-start p-5 rounded-2xl bg-white border border-[#DCDDEE] hover:shadow-lg hover:-translate-y-1 transition-all duration-200 text-left overflow-hidden"
                    style={{ borderTopWidth: 3, borderTopColor: p.accent }}
                  >
                    {/* Large watermark initial */}
                    <span
                      className="absolute -bottom-3 -right-2 text-[5rem] font-black leading-none select-none pointer-events-none transition-opacity duration-200 opacity-[0.06] group-hover:opacity-[0.1]"
                      style={{ color: p.accent }}
                    >
                      {getInitials(subject.name)[0]}
                    </span>

                    {/* Small initials badge */}
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 text-sm font-bold text-white flex-shrink-0"
                      style={{ background: p.accent }}
                    >
                      {getInitials(subject.name)}
                    </div>

                    {/* Subject name */}
                    <p className="relative text-sm font-semibold text-[#262A7A] leading-snug mb-3 group-hover:text-[#1D2045] transition-colors">
                      {subject.name}
                    </p>

                    {/* CTA */}
                    <span
                      className="relative inline-flex items-center gap-1 text-xs font-semibold mt-auto"
                      style={{ color: p.text }}
                    >
                      Find teachers
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
